import React, { useState, useEffect } from 'react';
import { Clock, MapPin, Phone, Droplet, Check, Calendar, Upload, X, Camera } from 'react-feather';
import { Hospital } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

const AcceptedRequests = () => {
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(''); 
  const { token } = useAuth();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [donationPhoto, setDonationPhoto] = useState(null);
  const [donationNotes, setDonationNotes] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submittingConfirmation, setSubmittingConfirmation] = useState(false);

  useEffect(() => {
    fetchAcceptedRequests();
  }, []);

  const fetchAcceptedRequests = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/requests/accepted');
      console.log('Fetched accepted requests:', response.data);
      
      // Log the status values for debugging
      console.log('Status values in response:', response.data.map(req => req.status));
      
      const processedRequests = response.data.map(request => {
        const createdDate = request.createdAt ? new Date(request.createdAt) : new Date();
        const formattedCreatedDate = createdDate.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        
        const formattedCreatedTime = createdDate.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit'
        });
        
        const acceptedDate = request.acceptedDate ? new Date(request.acceptedDate) : new Date();
        const formattedAcceptedDate = acceptedDate.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        
        const formattedAcceptedTime = acceptedDate.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit'
        });
        
        return {
          ...request,
          formattedCreatedDate,
          formattedCreatedTime,
          formattedAcceptedDate,
          formattedAcceptedTime
        };
      });
      
      // Filter out requests with 'donated' status
      const filteredRequests = processedRequests.filter(req => 
        req.status !== 'donated' && req.status !== 'Donated'
      );
      
      const sortedRequests = filteredRequests.sort((a, b) => {
        const dateA = new Date(a.acceptedDate || a.createdAt || 0);
        const dateB = new Date(b.acceptedDate || b.createdAt || 0);
        return dateB - dateA; 
      });
      
      setAcceptedRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching accepted requests:', error);
      setError('Failed to load your accepted requests. Please try again later.');
      toast.error('Failed to load your accepted requests');
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = statusFilter 
    ? acceptedRequests.filter(req => {
        console.log('Filtering request:', req.requestId, 'with status:', req.status, 'against filter:', statusFilter);
        // Handle case variations for status values
        if (statusFilter === 'rejected') {
          return req.status === 'rejected' || req.status === 'Rejected';
        } else if (statusFilter === 'pending_confirmation') {
          // Check for both 'pending_confirmation' and 'Pending Confirmation' (with space)
          return req.status === 'pending_confirmation' || 
                 req.status === 'Pending_confirmation' || 
                 req.status === 'Pending Confirmation';
        } else {
          return req.status === statusFilter;
        }
      })
    : acceptedRequests;

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return 'Not available';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Not available';
      }
      
      const formattedDate = date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      
      let hours = date.getHours();
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; 
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes} ${ampm}`;
      
      return `${formattedDate} at ${formattedTime}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Not available';
    }
  };

  const getStatusBadgeColor = (status, request) => {
    switch(status) {
      case 'accepted':
        return 'bg-blue-50 text-blue-700';
      case 'donated':
        return 'bg-green-50 text-green-700';
      case 'pending_confirmation':
        return 'bg-yellow-50 text-yellow-700';
      case 'pending':
        return request && request.needsReupload ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700';
      case 'rejected':
        return 'bg-red-50 text-red-700';
      case 'cancelled':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const getStatusDisplayText = (status) => {
    switch(status) {
      case 'pending_confirmation':
        return 'Pending Confirmation';
      case 'pending':
        return request => request.needsReupload ? 'Reupload Required' : 'Pending';
      case 'rejected':
        return 'Rejected';
      case 'donated':
        return 'Donated';
      default:
        return status;
    }
  };

  const openConfirmationModal = (request) => {
    setSelectedRequest(request);
    setDonationPhoto(null);
    setDonationNotes('');
    setShowConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    setSelectedRequest(null);
    setDonationPhoto(null);
    setDonationNotes('');
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo size must be less than 5MB');
        return;
      }
      
      if (!file.type.match('image.*')) {
        toast.error('Only image files are allowed');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setDonationPhoto({
          file: file,
          preview: event.target.result
        });
        toast.success('Image uploaded successfully! Click "Confirm Donation" to submit.', {
          icon: '📸',
          duration: 4000
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async (file) => {
    setUploadingPhoto(true);
    
    try {
      toast.loading('Uploading your donation proof...', {
        id: 'upload-toast'
      });
      
      const formData = new FormData();
      formData.append('photo', file);
      
      const response = await api.post('/upload', formData);
      
      toast.success('Photo uploaded successfully!', {
        id: 'upload-toast'
      });
      
      return response.data.url;
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload donation photo. Please try again.', {
        id: 'upload-toast'
      });
      throw new Error('Failed to upload donation photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const submitDonationConfirmation = async () => {
    if (!donationPhoto) {
      toast.error('Please upload a photo as proof of donation');
      return;
    }
    
    setSubmittingConfirmation(true);
    
    try {
      toast.loading('Submitting your donation confirmation...', {
        id: 'confirmation-toast'
      });
      
      const requestId = selectedRequest.requestId || selectedRequest.request || selectedRequest._id;
      
      if (!requestId) {
        throw new Error('Request ID not found');
      }
      
      const formData = new FormData();
      formData.append('donationProof', donationPhoto.file);
      formData.append('notes', donationNotes);
      
      const response = await api.post(`/requests/${requestId}/confirm-donation`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Donation confirmation response:', response.data);
      
      // Update the local state first
      setAcceptedRequests(prev => 
        prev.map(req => {
          const currentReqId = req.requestId || req.request || req._id;
          const selectedReqId = selectedRequest.requestId || selectedRequest.request || selectedRequest._id;
          
          return currentReqId === selectedReqId
            ? { 
                ...req, 
                status: 'pending_confirmation', 
                donationDate: new Date().toISOString(),
                donationProofPhoto: response.data.photoUrl || response.data.donationProofPhoto,
                notes: donationNotes
              }
            : req;
        })
      );
      
      // Make sure to close the modal before showing the success toast
      setShowConfirmationModal(false);
      setSelectedRequest(null);
      setDonationPhoto(null);
      setDonationNotes('');
      
      // Show a more detailed success notification
      toast.success(
        <div>
          <p className="font-medium">Donation confirmation submitted successfully!</p>
          <p className="text-sm mt-1">Thank you for your contribution. The requester will verify your donation soon.</p>
        </div>,
        {
          id: 'confirmation-toast',
          duration: 5000,
          icon: '🎉'
        }
      );
      
      // Fetch updated data after a short delay to ensure the modal is closed
      setTimeout(() => {
        fetchAcceptedRequests();
      }, 300);
    } catch (error) {
      console.error('Error confirming donation:', error);
      toast.error('Failed to confirm donation. Please try again.', {
        id: 'confirmation-toast'
      });
    } finally {
      setSubmittingConfirmation(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Accepted Requests</h2>
            <p className="text-gray-500 mt-1">Track and manage your accepted blood donation requests</p>
          </div>
          
          <select 
            className="px-4 py-2 bg-gray-50 border-none rounded-lg text-gray-600 text-sm focus:ring-2 focus:ring-red-500"
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">All Status</option>
            <option value="accepted">Accepted</option>
            <option value="pending_confirmation">Pending Confirmation</option>

          </select>
        </div>
      </div>

      {loading && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-gray-200 border-t-red-500 rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your accepted requests...</p>
        </div>
      )}

      {error && !loading && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <button 
            onClick={fetchAcceptedRequests}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && filteredRequests.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredRequests.map((request) => (
            <motion.div
              key={request.requestId || request.request || request._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <div className={`px-6 py-3 ${getStatusBadgeColor(request.status, request)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      request.status === 'accepted' ? 'bg-blue-500' :
                      request.status === 'donated' ? 'bg-green-500' :
                      request.status === 'pending_confirmation' ? 'bg-yellow-500' :
                      request.status === 'pending' && request.needsReupload ? 'bg-orange-500' :
                      request.status === 'rejected' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }`}></span>
                    <span className={`text-sm font-medium ${getStatusBadgeColor(request.status, request)}`}>
                      {typeof getStatusDisplayText(request.status) === 'function' 
                        ? getStatusDisplayText(request.status)(request) 
                        : getStatusDisplayText(request.status)}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">ID: {(request.requestId || request.request || request._id || '').toString().substring(0, 8)}</span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Droplet className="w-6 h-6 text-red-600" />
                  </div>

                  <div className="flex-grow">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">Blood Group {request.bloodGroup || request.bloodType || 'Unknown'}</h3>
                        <p className="text-sm text-gray-500">{request.requesterName || request.patientName || 'Requester'}</p>
                      </div>
                      <div className="bg-red-50 px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 flex items-center">
                        {request.unitsNeeded || request.units || 1} {(request.unitsNeeded || request.units || 1) > 1 ? 'units' : 'unit'}
                      </div>
                    </div>

                    <div className="mt-4 mb-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">
                          <span className="text-gray-500">Created: </span>
                          <span className="font-medium">{formatDateWithTime(request.createdAt)}</span>
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-gray-600">Accepted on</p>
                          <p className="font-medium text-gray-900">
                            {formatDateWithTime(request.acceptedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-gray-600">Donation Date</p>
                          <p className="font-medium text-gray-900">
                            {request.donationDate ? formatDate(request.donationDate) : 'Not scheduled'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Hospital className="w-4 h-4" />
                        <span className="text-sm">{request.hospitalName || 'Unknown Hospital'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{request.address || 'Unknown Address'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="text-sm">{request.location || 'Unknown Location'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span className="text-sm">{request.phone || 'Unknown Phone Number'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  {request.status !== 'donated' && request.status !== 'Donated' && (
                    <button 
                      className="flex-1 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                      onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(request.address || 'Unknown Address')}`, '_blank')}
                    >
                      <MapPin className="w-4 h-4" />
                      Get Directions
                    </button>
                  )}
                  
                  {(request.status === 'accepted' || (request.status === 'pending' && request.needsReupload)) && (
                    <button 
                      onClick={() => openConfirmationModal(request)}
                      className={`flex-1 ${request.needsReupload ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'} text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2`}
                    >
                      <Camera className="w-4 h-4" />
                      {request.needsReupload ? 'Reupload Proof' : 'Confirm Donation'}
                    </button>
                  )}
                  
                  {request.status === 'pending_confirmation' && (
                    <div className="mt-4 bg-yellow-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-yellow-600" />
                        <span className="text-sm font-medium text-yellow-700">Awaiting Requester Verification</span>
                      </div>
                      {request.donationDate && (
                        <p className="text-xs text-yellow-600 mt-1">
                          Submitted on {formatDate(request.donationDate)}
                        </p>
                      )}
                      <div className="mt-2 flex items-center">
                        <img 
                          src={request.donationProofPhoto} 
                          alt="Donation proof" 
                          className="w-12 h-12 object-cover rounded-md mr-2" 
                        />
                        <p className="text-xs text-gray-600">
                          Proof photo submitted
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {request.status === 'donated' && (
                    <div className="mt-4 bg-green-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-700">Donation Confirmed</span>
                      </div>
                      {request.donationDate && (
                        <p className="text-xs text-green-600 mt-1">
                          Donated on {formatDate(request.donationDate)}
                        </p>
                      )}
                      {request.requesterFeedback && (
                        <p className="text-xs text-gray-600 mt-2 italic">
                          "{request.requesterFeedback}"
                        </p>
                      )}
                    </div>
                  )}

                  {request.status === 'rejected' && (
                    <div className="mt-4 bg-red-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <X className="h-5 w-5 text-red-600" />
                        <span className="text-sm font-medium text-red-700">Donation Not Verified</span>
                      </div>
                      {request.requesterFeedback && (
                        <p className="text-xs text-gray-600 mt-2 italic">
                          "{request.requesterFeedback}"
                        </p>
                      )}
                    </div>
                  )}
                  
                  {request.status === 'pending' && request.needsReupload && (
                    <div className="mt-4 bg-orange-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Upload className="h-5 w-5 text-orange-600" />
                        <span className="text-sm font-medium text-orange-700">Reupload Required</span>
                      </div>
                      {request.requesterFeedback && (
                        <p className="text-xs text-gray-600 mt-2 italic">
                          "Requester feedback: {request.requesterFeedback}"
                        </p>
                      )}
                      <p className="text-xs text-orange-600 mt-1">
                        Please upload a clearer or different proof of donation.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Show rejection message if request was rejected because another donor was verified */}
              {request.status === 'rejected' && request.requesterFeedback && request.requesterFeedback.includes('already been received') && (
                <div className="px-4 py-3 bg-red-50 border-l-4 border-red-500">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">
                        {request.requesterFeedback}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {!loading && !error && filteredRequests.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl shadow-sm p-8 text-center"
        >
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No Accepted Requests</h3>
          <p className="mt-2 text-sm text-gray-500">
            {statusFilter 
              ? `You don't have any requests with "${statusFilter}" status.` 
              : "You haven't accepted any blood donation requests yet."}
          </p>
          {statusFilter && (
            <button 
              onClick={() => setStatusFilter('')}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filter
            </button>
          )}
        </motion.div>
      )}

      {showConfirmationModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedRequest.needsReupload ? 'Reupload Donation Proof' : 'Confirm Blood Donation'}
                </h3>
                <button 
                  onClick={closeConfirmationModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  {selectedRequest.needsReupload 
                    ? 'The requester has asked for a clearer or different proof of your donation. Please upload a new photo.'
                    : 'Please upload a photo as proof of your donation (e.g., donation certificate, bandage on arm, or hospital receipt).'}
                </p>
                
                {selectedRequest.needsReupload && selectedRequest.requesterFeedback && (
                  <div className="mb-4 p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-700 font-medium">Requester Feedback:</p>
                    <p className="text-sm text-gray-600 mt-1 italic">"{selectedRequest.requesterFeedback}"</p>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Donation Photo*
                  </label>
                  
                  {donationPhoto ? (
                    <div className="relative">
                      <img 
                        src={donationPhoto.preview} 
                        alt="Donation proof" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <button 
                        onClick={() => {
                          setDonationPhoto(null);
                          toast.info('Photo removed. Please upload a new one.');
                        }}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="mt-2 bg-green-50 p-2 rounded-lg">
                        <p className="text-sm text-green-700 flex items-center">
                          <Check className="w-4 h-4 mr-1" /> 
                          Photo uploaded successfully! Click "Confirm Donation" to submit.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50"
                      onClick={() => document.getElementById('donation-photo-input').click()}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, JPEG (max 5MB)</p>
                    </div>
                  )}
                  
                  <input 
                    type="file" 
                    id="donation-photo-input" 
                    accept="image/*" 
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={donationNotes}
                    onChange={(e) => setDonationNotes(e.target.value)}
                    placeholder="Add any additional information about your donation..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={closeConfirmationModal}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={submittingConfirmation || uploadingPhoto}
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    submitDonationConfirmation();
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                  disabled={!donationPhoto || submittingConfirmation || uploadingPhoto}
                >
                  {(submittingConfirmation || uploadingPhoto) ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      {uploadingPhoto ? 'Uploading...' : 'Confirming...'}
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Confirm Donation
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AcceptedRequests;